"use client";

import React from "react";
import { motion } from "framer-motion";
import { Flame, Wrench, ShieldCheck, Gauge, Sparkles, Award, Clock, MapPin, Phone, MessageCircle, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PHONE_LOCAL, PHONE_TEL_HREF, WHATSAPP_HREF } from "@/lib/contact";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
interface ServiceItem {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
interface AdvantageItem {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
const serviceItems: ServiceItem[] = [{
  id: "connections",
  title: "فحص تمديدات ووصلات الغاز",
  description: "معاينة دقيقة لخطوط الغاز، المحابس، والوصلات المرنة للكشف عن أي تسريب محتمل وضمان سلامة الإمداد.",
  icon: Flame
}, {
  id: "pressure",
  title: "اختبار وتعيير ضغط وتدفق الغاز",
  description: "قياس ضغط الشعلات وتوزيع تدفق الغاز بدقة للحصول على شعلة زرقاء متوازنة وأعلى كفاءة طهي.",
  icon: Gauge
}, {
  id: "ignition",
  title: "إصلاح أعطال الإشعال الذاتي",
  description: "تشخيص ومعالجة تلف مولدات الشرر (الإشعال الذاتي)، الشموع، والمفاتيح الكهربائية بدقة وأمان.",
  icon: Sparkles
}, {
  id: "burners",
  title: "تنظيف وتسليك عيون ومنافذ اللهب",
  description: "إزالة الشحوم والكربون المتراكم في فونيات وعيون الغاز لتحسين توزيع الحرارة وسرعة الاشتعال.",
  icon: Wrench
}, {
  id: "sensors",
  title: "فحص حساسات الأمان والعزل الكهربائي",
  description: "التحقق من صمامات الأمان وثرموكبل الإغلاق التلقائي والعزل الحراري وفق معايير السلامة المعتمدة.",
  icon: ShieldCheck
}];
const advantages: AdvantageItem[] = [{
  id: "expertise",
  title: "خبرة متخصصة ومعايير أمان",
  description: "معرفة عميقة بمختلف موديلات الأفران الغازية والتعامل الصارم مع بروتوكولات الأمان.",
  icon: Award
}, {
  id: "technicians",
  title: "فنيون معتمدون ومحترفون",
  description: "طاقم فني مدرب على أدق تفاصيل فحص ومعالجة أعطال الغاز في المنزل بدون نقل الجهاز.",
  icon: ShieldCheck
}, {
  id: "speed",
  title: "زيارات منزلية سريعة بالرياض",
  description: "استجابة فورية ومواعيد دقيقة لتغطية الأعطال الطارئة في جميع أحياء العاصمة.",
  icon: Clock
}, {
  id: "warranty",
  title: "ضمان شامل لقطع الغيار والصيانة",
  description: "ضمان معتمد على أعمال الصيانة وقطع الغيار الأصلية المركبة لراحة بال العميل.",
  icon: Wrench
}];
const faqs: FaqItem[] = [{
  id: "safety",
  question: "ما هي تدابير السلامة المتبعة أثناء صيانة فرن الغاز؟",
  answer: "نقوم بإغلاق مصدر الغاز الرئيسي واستخدام أجهزة كشف التسرب الإلكترونية قبل البدء، بالإضافة إلى التأكد من إحكام الرباط واستبدال الوصلات التالفة واختبار حساس الأمان قبل إعادة التشغيل."
}, {
  id: "frequency",
  question: "كم مرة يُنصح بإجراء فحص وصيانة دورية لفرن الغاز؟",
  answer: "يُفضل عمل فحص وقائي مرة كل 6 إلى 12 شهراً للتأكد من سلامة عيون الشعلة، توازن تدفق الغاز، وتنظيف الفونيات لتجنب تراكم الدهون وضمان السلامة المنزلية المستمرة."
}, {
  id: "parts",
  question: "هل توفرون قطع غيار أصلية للأفران الغازية؟",
  answer: "نعم، نوفر قطع غيار أصلية ومطابقة للمواصفات لكافة العلامات التجارية بما في ذلك المفاتيح، الحساسات، الشموع، ومحابس الغاز مع ضمان الجودة."
}, {
  id: "warranty-policy",
  question: "ما هي شروط وضمانات الصيانة المقدمة؟",
  answer: "نمنح ضماناً كتابياً يغطي الصيانة المنفذة وقطع الغيار المستبدلة ضد عيوب التركيب والتصنيع، مع التزام فوري بمعالجة أي ملاحظة خلال فترة الضمان."
}, {
  id: "booking",
  question: "كيف يمكنني حجز موعد صيانة فرن غاز في الرياض؟",
  answer: "يمكنك حجز الموعد مباشرة عبر الاتصال الهاتفي أو من خلال محادثة واتساب الفورية، وتحديد الوقت المناسب لزيارة الفني إلى منزلك."
}];
export default function GasOvenMaintenanceService() {
  return <section id="gasovenmaintenanceservice" data-nav="light" dir="rtl" className="bg-background text-foreground py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-20 sm:space-y-28">
        {/* Block 1: Detailed Service Specifications */}
        <div className="space-y-10">
          <div className="max-w-3xl">
            <div className="w-8 h-[3px] bg-primary mb-4 rounded-full" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
              خدمات صيانة أفران الغاز الشاملة في الرياض
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              فحص فني دقيق لمعايير الأمان وتشغيل شعلات الغاز بأعلى كفاءة منزلية
              متخصصة عبر أحدث معدات الفحص والسلامة.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceItems.map((item, index) => {
            const Icon = item.icon;
            return <motion.div key={item.id} data-index={index} initial={{
              opacity: 0,
              y: 14
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.45,
              delay: index * 0.08
            }}>
                  <Card className="h-full border border-border shadow-xs hover:shadow-sm transition-all duration-200">
                    <CardHeader className="space-y-4">
                      <div className="size-12 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                        <Icon className="size-6 text-primary" />
                      </div>
                      <CardTitle className="text-xl font-bold text-foreground">
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Block 2: Why Choose لمسة احتراف */}
        <div className="space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-3">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
              لماذا تختار لمسة احتراف لصيانة فرن الغاز؟
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base">
              معايير سلامة صارمة وسرعة استجابة تضمن أمان أسرتك وكفاءة مطبخك
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {advantages.map((adv, index) => {
            const Icon = adv.icon;
            return <motion.div key={adv.id} data-index={index} initial={{
              opacity: 0,
              y: 14
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.45,
              delay: index * 0.08
            }} className="bg-card border border-border rounded-xl p-6 flex flex-col items-start gap-4 shadow-2xs hover:border-primary/40 transition-colors">
                  <div className="size-10 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Icon className="size-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-foreground">
                      {adv.title}
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                      {adv.description}
                    </p>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Block 3: Coverage Area */}
        <div className="rounded-2xl bg-muted border border-border p-6 sm:p-10 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8 space-y-4">
              <div className="inline-flex items-center gap-2 text-primary font-medium text-sm">
                <MapPin className="size-4 shrink-0" />
                <span>زيارات صيانة منزلية فورية</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
                تغطية شاملة لجميع أحياء ومناطق الرياض
              </h2>
              <p className="text-muted-foreground leading-relaxed text-sm sm:text-base max-w-3xl">
                نصلكم أينما كنتم داخل العاصمة الرياض: شمال الرياض (الملقا،
                الياسمين، النخيل، العقيق، الصحافة)، شرق الرياض، غرب الرياض، وجنوب
                الرياض مع طواقم صيانة مجهزة بالمعدات لتقديم الخدمة في نفس اليوم.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-center">
              <Button asChild size="lg" className="w-full justify-center">
                <a href={PHONE_TEL_HREF} className="flex items-center gap-2">
                  <Phone className="size-4 shrink-0" />
                  <span>طلب فني لمنزلك الآن</span>
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full justify-center bg-background">
                <Link to="/ServicesPage" className="flex items-center justify-center">
                  استعراض كافة خدماتنا
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Block 4: FAQ Accordion */}
        <div className="space-y-8 max-w-4xl mx-auto">
          <div className="text-center space-y-3">
            <div className="inline-flex items-center gap-2 text-primary text-sm font-semibold">
              <HelpCircle className="size-4" />
              <span>إجابات مهمة قبل الصيانة</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
              الأسئلة الشائعة حول صيانة أفران الغاز
            </h2>
          </div>

          <Accordion type="single" collapsible defaultValue="safety" className="w-full bg-card border border-border rounded-xl px-6 py-2 shadow-2xs divide-y divide-border">
            {faqs.map((faq, index) => <AccordionItem key={faq.id} value={faq.id} data-index={index} className="border-none py-2">
                <AccordionTrigger className="text-base sm:text-lg font-semibold hover:no-underline hover:text-primary transition-colors text-right">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-2 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        {/* Block 5: Call to Action Block */}
        <div className="rounded-2xl bg-secondary text-secondary-foreground border border-secondary/40 p-8 sm:p-12 shadow-md relative overflow-hidden">
          <div className="max-w-3xl space-y-4">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-secondary-foreground">
              احجز موعد صيانة فرن الغاز الآن في الرياض
            </h2>
            <p className="text-secondary-foreground/80 text-sm sm:text-base leading-relaxed max-w-2xl">
              تواصل مباشرة مع فريق لمسة احتراف لصيانة آمنة وفورية بضمان معتمد.
              فريقنا جاهز لخدمتكم طوال الأسبوع مع حلول سريعة وفحص دقيق لمنزلك.
            </p>

            <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-6 shadow-sm">
                <Link to={WHATSAPP_HREF} newTab className="flex items-center justify-center gap-2">
                  <MessageCircle className="size-5 shrink-0" />
                  <span>حجز موعد عبر واتساب</span>
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="bg-secondary/40 text-secondary-foreground border-secondary-foreground/20 hover:bg-secondary/60 hover:text-secondary-foreground font-medium px-6">
                <a href={PHONE_TEL_HREF} className="flex items-center justify-center gap-2">
                  <Phone className="size-5 shrink-0" />
                  <span dir="ltr">+966 53 818 0411</span>
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}